import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { ChevronRight, CheckCircle2, Award, Users, Factory, ShieldCheck, Play } from "lucide-react";
import { contact, stats } from "../mock";
import StatsBar from "../components/StatsBar";
import Certifications from "../components/Certifications";
import WhyChooseUs from "../components/WhyChooseUs";
import Gallery from "../components/Gallery";
import CtaCallback from "../components/CtaCallback";
import useSeo from "../hooks/useSeo";

export default function About() {
  useSeo({
    title: "About Us",
    description: "Ess Bee Heaters Pvt. Ltd. — 25+ years of engineering excellence in manufacturing industrial shortwave IR heaters. ISO 9001:2015 certified, CE marked, RoHS compliant.",
    keywords: "about Ess Bee Heaters, IR heater manufacturer India, industrial heater company Delhi, ISO 9001 certified manufacturer",
    path: "/about",
  });
  const [playing, setPlaying] = React.useState(false);
  return (
    <main>
      <PageHero
        tagline="Our Story"
        title="About"
        highlight="Us"
        subtitle="25+ years of engineering excellence in industrial heating elements."
        crumb={(
          <div className="flex items-center gap-2">
            <Link to="/" className="hover:text-orange-500">Home</Link>
            <ChevronRight size={14} className="text-orange-500" />
            <span className="text-orange-500 font-semibold">About Us</span>
          </div>
        )}
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] tracking-[0.3em] text-orange-600 font-semibold uppercase">Welcome To</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 uppercase">Welcome to Ess Bee Heaters Pvt. Ltd.</h2>
            <span className="block w-20 h-[3px] bg-orange-500 mt-4" />
            <p className="mt-6 text-neutral-700 leading-relaxed">
              We are a trusted and pioneering manufacturer of Commercial &amp; Industrial Heaters &amp; Heating Elements, delivering high-performance heating solutions powered by advanced technology and decades of technical expertise. Our focus on innovation, efficiency, and reliability has made us a preferred partner for customers seeking consistent performance and long service life for their equipment and machinery.
            </p>
            <p className="mt-4 text-neutral-700 leading-relaxed">
              Our extensive product range includes shortwave infrared heaters and shortwave infrared heaters with reflector boxes. Each product is engineered for maximum thermal efficiency and can be custom-designed to meet specific operational and application requirements.
            </p>
            <div className="grid grid-cols-2 gap-3 mt-6 max-w-md">
              {["ISO 9001:2015 Certified", "RoHS Compliant", "CE Certified", "In-House R&D Lab", "OEM Solutions", "Pan India Delivery"].map(t => (
                <div key={t} className="flex items-center gap-2 text-sm text-neutral-800">
                  <CheckCircle2 size={18} className="text-orange-500 shrink-0" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="relative rounded-xl overflow-hidden shadow-xl ring-1 ring-black/5 bg-black aspect-video">
              {playing ? (
                <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${contact.youtubeId}?autoplay=1&rel=0`} title="About Video" allow="autoplay; encrypted-media" allowFullScreen />
              ) : (
                <button onClick={() => setPlaying(true)} className="group w-full h-full relative">
                  <img src={`https://img.youtube.com/vi/${contact.youtubeId}/maxresdefault.jpg`} alt="video" className="w-full h-full object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40">
                    <span className="w-20 h-20 rounded-full bg-orange-500 text-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                      <Play size={32} className="ml-1" fill="white" />
                    </span>
                  </span>
                </button>
              )}
            </div>
            <div className="absolute -bottom-6 -right-2 md:right-8 bg-orange-500 text-white px-6 py-4 rounded-lg shadow-xl">
              <p className="font-display text-3xl font-bold leading-none">25+</p>
              <p className="text-xs uppercase tracking-wider mt-1">Years of Excellence</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-neutral-900 text-white bg-dotted-dark">
        <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { v: "25+", l: "Years of Excellence", I: Award },
            { v: "1000+", l: "Happy Clients", I: Users },
            { v: "50+", l: "Industries Served", I: Factory },
            { v: "100%", l: "Quality Assured", I: ShieldCheck },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-4">
              <span className="w-14 h-14 rounded-full bg-orange-500/10 ring-1 ring-orange-500/40 flex items-center justify-center text-orange-500">
                <s.I size={26} />
              </span>
              <div>
                <p className="font-display text-3xl md:text-4xl font-bold leading-none text-orange-500">{s.v}</p>
                <p className="uppercase tracking-wider text-xs md:text-sm text-neutral-300 mt-2">{s.l}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-cream bg-dotted">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {[
            { title: "Our Mission", body: "To deliver world-class industrial heating solutions powered by innovation, precision, and uncompromising quality." },
            { title: "Our Vision", body: "To be India's most trusted manufacturer of shortwave IR heaters, recognised globally for engineering excellence." },
            { title: "Our Values", body: "Integrity, customer obsession, continuous improvement, and absolute commitment to safety and standards." },
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-2xl p-7 border border-orange-100 lift-on-hover">
              <span className="inline-block px-3 py-1 rounded-full bg-orange-100 text-orange-600 text-[10px] font-bold tracking-widest uppercase">0{i+1}</span>
              <h3 className="font-display text-2xl uppercase mt-4">{c.title}</h3>
              <span className="block w-12 h-[3px] bg-orange-500 mt-3 mb-4" />
              <p className="text-neutral-700 leading-relaxed text-sm">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <Certifications />
      <WhyChooseUs />
      <Gallery />
      <CtaCallback />
    </main>
  );
}
